import { useEffect, useMemo } from 'react';
import { useParams } from 'react-router-dom';
import { Form, ModalProps } from 'antd';
import { TreeSelectProps } from 'antd/lib';
import { isObjectLike } from 'lodash';

import { FormModal } from 'components';

import { usePatchRequest } from 'hooks';
import { mapTreeSelectIds } from 'utils';

import { AggregatedProductIdentity, AggregatedProductIdentityUpdate, ProductIdentityValue } from 'codegen';

import { fields } from './lib';

const { useForm, useWatch } = Form;

export const EditIdentityModal = ({
  open,
  record,
  retailers,
  identity,
  refresh,
  onCancel,
}: Omit<ModalProps, 'onCancel'> & {
  record: AggregatedProductIdentity & { id: number };
  identity: ProductIdentityValue;
  retailers: TreeSelectProps['treeData'],
  refresh: () => void;
  onCancel: () => void;
}) => {
  const { productId } = useParams();

  const [form] = useForm<AggregatedProductIdentity & { value: string }>();
  const type = useWatch('type', form);

  const [patchIdentities, { loading }] = usePatchRequest<AggregatedProductIdentity, AggregatedProductIdentityUpdate>({
    url: `/products/details/${productId}/identities`,
    options: {
      onSuccess: () => {
        refresh();
        onCancel();
      },
    },
  });

  const isTypeAI = type === AggregatedProductIdentity.type.AI;
  const initValues = useMemo(() => ({ ...record, value: identity.value }), [record, identity]);

  useEffect(() => {
    if (isTypeAI) {
      form.resetFields(['fullMatchThreshold', 'partialMatchThreshold']);
      form.setFieldValue('partialMatchThreshold', undefined);
      form.setFieldValue('fullMatchThreshold', undefined);
    }
  }, [isTypeAI]);

  return (
    <FormModal
      open={open}
      form={form}
      title='Edit Identity'
      fields={fields({ retailers, isTypeAI })}
      onCancel={onCancel}
      initialValues={initValues}
      onFinish={(formData) => {
        const newValues = record.values.reduce<ProductIdentityValue[]>((acc, item) => {
          if (identity.id === item.id) {
            acc.push({ ...item, value: formData.value });
          }

          return acc;
        }, []);

        const formattedRetailers = formData.retailers.some(isObjectLike)
          ? formData.retailers.map((merchant) => ({ id: mapTreeSelectIds(merchant.id) }))
          : formData.retailers.map((ID) => ({ id: mapTreeSelectIds(String(ID)) }));

        patchIdentities({
          payload: {
            ...formData,
            values: newValues,
            retailers: formattedRetailers,
          },
        });
      }}
      buttonProps={{
        loading,
        label: 'Edit',
      }}
    />
  );
};
